import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import {
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  Minus,
  History,
  Calculator,
  Printer,
  FileText,
  MessageCircle,
} from "lucide-react";

const API = `${window.location.origin}/api`;

// دالة مشاركة وصل الصندوق عبر واتساب
const shareCashboxViaWhatsApp = (data, shopName) => {
  let text = `*${shopName || "اسم المؤسسة"}*\n`;
  text += `${"═".repeat(20)}\n\n`;
  
  if (data.type === "open") {
    text += `📂 *وصل فتح الصندوق*\n\n`;
    text += `🔢 رقم الوصل: ${data.receiptNumber}\n`;
    text += `💵 المبلغ الافتتاحي: ${data.amount?.toLocaleString()} د.ع\n`;
    text += `👤 الموظف: ${data.employeeName}\n`;
    text += `📅 التاريخ: ${data.date}\n`;
    if (data.notes) text += `📝 ملاحظات: ${data.notes}\n`;
  } else {
    text += `📕 *وصل إغلاق الصندوق*\n\n`;
    text += `🔢 رقم الوصل: ${data.receiptNumber}\n`;
    text += `💵 الافتتاحي: ${data.openingAmount?.toLocaleString()} د.ع\n`;
    text += `📈 المبيعات: +${data.cashSales?.toLocaleString()} د.ع\n`;
    text += `📉 المصروفات: -${data.expenses?.toLocaleString()} د.ع\n`;
    text += `📊 المتوقع: ${data.expectedAmount?.toLocaleString()} د.ع\n`;
    text += `💰 *الفعلي: ${data.actualAmount?.toLocaleString()} د.ع*\n`;
    text += `${data.difference > 0 ? '✅' : data.difference < 0 ? '⚠️' : '✔️'} الفرق: ${data.difference > 0 ? '+' : ''}${data.difference?.toLocaleString()} د.ع\n`;
    text += `👤 الموظف: ${data.employeeName}\n`;
    text += `📅 التاريخ: ${data.date}\n`;
    if (data.notes) text += `📝 ملاحظات: ${data.notes}\n`;
  }
  
  text += `\n${"═".repeat(20)}\n`;
  text += `_Powered by Nazario-Hesabaty_`;
  
  const encodedText = encodeURIComponent(text);
  window.open(`https://wa.me/?text=${encodedText}`, "_blank");
};

export default function CashRegisterPage() {
  const { user } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const isArabic = language === "ar";

  const [currentRegister, setCurrentRegister] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpenDialogOpen, setIsOpenDialogOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [actualAmount, setActualAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [closingSummary, setClosingSummary] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [registerCounter, setRegisterCounter] = useState(0);

  useEffect(() => {
    fetchData();
    fetchRegisterCounter();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API}/cash-register/current`),
        axios.get(`${API}/cash-register/history?limit=30`)
      ]);
      setCurrentRegister(currentRes.data);
      setHistory(historyRes.data || []);
    } catch (error) {
      console.error("Error fetching cash register data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegisterCounter = async () => {
    try {
      const response = await axios.get(`${API}/cash-register/counter`);
      setRegisterCounter(response.data.count || 0);
    } catch (error) {
      console.log("Counter not found");
    }
  };

  const handleOpenRegister = async () => {
    if (!openingAmount) {
      toast.error("أدخل المبلغ الافتتاحي");
      return;
    }
    if (!employeeName) {
      toast.error("أدخل اسم الموظف");
      return;
    }

    try {
      const response = await axios.post(`${API}/cash-register/open`, {
        opening_amount: parseFloat(openingAmount),
        notes: notes,
        employee_name: employeeName
      });
      
      const newCounter = registerCounter + 1;
      setReceiptData({
        type: "open",
        receiptNumber: `C${String(newCounter).padStart(7, '0')}`,
        amount: parseFloat(openingAmount),
        employeeName: employeeName,
        date: new Date().toLocaleDateString('ar-IQ', { 
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit'
        }),
        notes: notes
      });
      setRegisterCounter(newCounter);
      
      setCurrentRegister(response.data);
      setIsOpenDialogOpen(false);
      setOpeningAmount("");
      setNotes("");
      setEmployeeName("");
      setShowReceipt(true);
      toast.success("تم فتح الصندوق بنجاح");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في فتح الصندوق");
    }
  };

  const handleCloseRegister = async () => {
    if (!actualAmount) {
      toast.error("أدخل المبلغ الفعلي");
      return;
    }
    if (!employeeName) {
      toast.error("أدخل اسم الموظف");
      return;
    }

    try {
      const response = await axios.post(`${API}/cash-register/close`, {
        actual_amount: parseFloat(actualAmount),
        notes: notes,
        employee_name: employeeName
      });
      
      const summary = response.data.summary;
      setClosingSummary(summary);
      
      const newCounter = registerCounter + 1;
      setReceiptData({
        type: "close",
        receiptNumber: `C${String(newCounter).padStart(7, '0')}`,
        openingAmount: summary.opening_amount,
        cashSales: summary.total_cash_sales,
        expenses: summary.total_expenses,
        expectedAmount: summary.expected_amount,
        actualAmount: parseFloat(actualAmount),
        difference: summary.difference,
        employeeName: employeeName,
        date: new Date().toLocaleDateString('ar-IQ', { 
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit'
        }),
        notes: notes
      });
      setRegisterCounter(newCounter);
      
      setCurrentRegister(null);
      setIsCloseDialogOpen(false);
      setActualAmount("");
      setNotes("");
      setEmployeeName("");
      setShowReceipt(true);
      toast.success("تم إغلاق الصندوق بنجاح");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في إغلاق الصندوق");
    }
  };

  const getDifferenceColor = (diff) => {
    if (diff > 0) return "text-green-400";
    if (diff < 0) return "text-red-400";
    return "text-blue-400";
  };

  const getDifferenceIcon = (diff) => {
    if (diff > 0) return <TrendingUp className="w-5 h-5 text-green-400" />;
    if (diff < 0) return <TrendingDown className="w-5 h-5 text-red-400" />;
    return <CheckCircle className="w-5 h-5 text-blue-400" />;
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="cash-register-page">
      {/* رأس الصفحة */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
          <Wallet className="w-7 h-7 text-amber-400" />
          {isArabic ? "صندوق النقدية" : "Cash Register"}
        </h1>
      </div>

      {/* حالة الصندوق الحالية */}
      <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            {currentRegister ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-400" />
                {isArabic ? "الصندوق مفتوح" : "Register Open"}
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-amber-400" />
                {isArabic ? "الصندوق مغلق" : "Register Closed"}
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentRegister ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <DollarSign className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">{isArabic ? "المبلغ الافتتاحي" : "Opening Amount"}</p>
                  <p className="text-emerald-400 text-xl font-bold">{formatCurrency(currentRegister.opening_amount)}</p>
                  <USDHint amount={currentRegister.opening_amount} />
                </div>
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-center">
                  <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">{isArabic ? "وقت الفتح" : "Opening Time"}</p>
                  <p className="text-blue-400 text-lg font-bold">
                    {new Date(currentRegister.opening_time).toLocaleTimeString(isArabic ? "ar-IQ" : "en-US", { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 text-center">
                  <History className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">{isArabic ? "فتح بواسطة" : "Opened By"}</p>
                  <p className="text-purple-400 text-lg font-bold truncate">{currentRegister.opened_by}</p>
                </div>
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center">
                  <Calculator className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">{isArabic ? "الحالة" : "Status"}</p>
                  <p className="text-amber-400 text-lg font-bold">{isArabic ? "قيد التشغيل" : "Active"}</p>
                </div>
              </div>

              <Button
                onClick={() => setIsCloseDialogOpen(true)}
                className="w-full bg-red-600 hover:bg-red-700 text-lg py-6"
              >
                <Minus className="w-5 h-5 me-2" />
                {isArabic ? "إغلاق الصندوق" : "Close Register"}
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Wallet className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-6">
                {isArabic ? "الصندوق مغلق حالياً. افتح صندوقاً جديداً لبدء العمل." : "Register is closed. Open a new register to start."}
              </p>
              <Button
                onClick={() => setIsOpenDialogOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6"
              >
                <Plus className="w-5 h-5 me-2" />
                {isArabic ? "فتح الصندوق" : "Open Register"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ملخص آخر إغلاق */}
      {closingSummary && (
        <Card className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-400" />
              {isArabic ? "ملخص الإغلاق الأخير" : "Last Closing Summary"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-slate-400 text-xs">{isArabic ? "المبلغ الافتتاحي" : "Opening"}</p>
                <p className="text-white font-bold">{formatCurrency(closingSummary.opening_amount)}</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-slate-400 text-xs">{isArabic ? "المبيعات النقدية" : "Cash Sales"}</p>
                <p className="text-emerald-400 font-bold">+{formatCurrency(closingSummary.total_cash_sales)}</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-slate-400 text-xs">{isArabic ? "المصروفات" : "Expenses"}</p>
                <p className="text-red-400 font-bold">-{formatCurrency(closingSummary.total_expenses)}</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-slate-400 text-xs">{isArabic ? "المتوقع" : "Expected"}</p>
                <p className="text-blue-400 font-bold">{formatCurrency(closingSummary.expected_amount)}</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-slate-400 text-xs">{isArabic ? "الفعلي" : "Actual"}</p>
                <p className="text-purple-400 font-bold">{formatCurrency(closingSummary.actual_amount)}</p>
                <USDHint amount={closingSummary.actual_amount} />
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-slate-400 text-xs">{isArabic ? "الفرق" : "Difference"}</p>
                <p className={`font-bold ${getDifferenceColor(closingSummary.difference)}`}>
                  {closingSummary.difference > 0 ? "+" : ""}{formatCurrency(closingSummary.difference)}
                </p>
                <USDHint amount={Math.abs(closingSummary.difference)} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* سجل الصندوق */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <History className="w-5 h-5 text-slate-400" />
            {isArabic ? "سجل الصندوق" : "Register History"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
              {isArabic ? "لا يوجد سجل بعد" : "No history yet"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">{isArabic ? "التاريخ" : "Date"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "الافتتاحي" : "Opening"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "المبيعات" : "Sales"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "المصروفات" : "Expenses"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "المتوقع" : "Expected"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "الفعلي" : "Actual"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "الفرق" : "Diff"}</TableHead>
                    <TableHead className="text-slate-400">{isArabic ? "الحالة" : "Status"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((record, index) => (
                    <TableRow key={record.id || index} className="border-slate-700">
                      <TableCell className="text-white">{record.date}</TableCell>
                      <TableCell className="text-slate-300">{formatCurrency(record.opening_amount || 0)}</TableCell>
                      <TableCell className="text-emerald-400">{formatCurrency(record.total_cash_sales || 0)}</TableCell>
                      <TableCell className="text-red-400">{formatCurrency(record.total_expenses || 0)}</TableCell>
                      <TableCell className="text-blue-400">{formatCurrency(record.expected_amount || 0)}</TableCell>
                      <TableCell className="text-purple-400">{formatCurrency(record.actual_amount || 0)}</TableCell>
                      <TableCell className={getDifferenceColor(record.difference || 0)}>
                        <div className="flex items-center gap-1">
                          {getDifferenceIcon(record.difference || 0)}
                          {formatCurrency(Math.abs(record.difference || 0))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          record.status === 'open' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-slate-500/20 text-slate-400'
                        }`}>
                          {record.status === 'open' 
                            ? (isArabic ? 'مفتوح' : 'Open') 
                            : (isArabic ? 'مغلق' : 'Closed')}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog فتح الصندوق */}
      <Dialog open={isOpenDialogOpen} onOpenChange={setIsOpenDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-400" />
              فتح صندوق جديد
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-200">المبلغ الافتتاحي</Label>
              <PriceInput
                value={parseFloat(openingAmount) || 0}
                onChange={(v) => setOpeningAmount(String(v))}
                placeholder="0"
                testId="opening-amount-input"
                className="bg-slate-900 border-slate-700 text-white text-lg"
              />
              <p className="text-xs text-slate-500">
                المبلغ الموجود في الصندوق عند بداية اليوم
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">اسم الموظف</Label>
              <Input
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="اسم الموظف المسؤول"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">ملاحظات (اختياري)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpenDialogOpen(false)} className="border-slate-600">
              إلغاء
            </Button>
            <Button onClick={handleOpenRegister} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 me-2" />
              فتح الصندوق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog إغلاق الصندوق */}
      <Dialog open={isCloseDialogOpen} onOpenChange={setIsCloseDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Minus className="w-5 h-5 text-red-400" />
              إغلاق الصندوق
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-400 text-sm">
                قم بعدّ النقود في الصندوق وأدخل المبلغ الفعلي
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">المبلغ الفعلي في الصندوق</Label>
              <PriceInput
                value={parseFloat(actualAmount) || 0}
                onChange={(v) => setActualAmount(String(v))}
                placeholder="0"
                testId="actual-amount-input"
                className="bg-slate-900 border-slate-700 text-white text-lg"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">اسم الموظف</Label>
              <Input
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="اسم الموظف المسؤول"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">ملاحظات (اختياري)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCloseDialogOpen(false)} className="border-slate-600">
              إلغاء
            </Button>
            <Button onClick={handleCloseRegister} className="bg-red-600 hover:bg-red-700">
              <Minus className="w-4 h-4 me-2" />
              إغلاق الصندوق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-400" />
              {receiptData?.type === "open" ? "وصل فتح الصندوق" : "وصل إغلاق الصندوق"}
            </DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-slate-800 rounded-xl border border-slate-700">
                <p className="text-slate-400 text-sm">رقم الوصل</p>
                <p className="text-2xl font-bold text-amber-400">{receiptData.receiptNumber}</p>
              </div>
              
              {receiptData.type === "open" ? (
                <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                  <p className="text-slate-400 text-sm">المبلغ الافتتاحي</p>
                  <p className="text-3xl font-bold text-emerald-400">{formatCurrency(receiptData.amount)}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-800 rounded-lg text-center">
                      <p className="text-slate-400 text-xs">الافتتاحي</p>
                      <p className="text-white font-bold">{formatCurrency(receiptData.openingAmount)}</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg text-center">
                      <p className="text-slate-400 text-xs">المبيعات</p>
                      <p className="text-emerald-400 font-bold">+{formatCurrency(receiptData.cashSales)}</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg text-center">
                      <p className="text-slate-400 text-xs">المصروفات</p>
                      <p className="text-red-400 font-bold">-{formatCurrency(receiptData.expenses)}</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg text-center">
                      <p className="text-slate-400 text-xs">المتوقع</p>
                      <p className="text-blue-400 font-bold">{formatCurrency(receiptData.expectedAmount)}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/30 text-center">
                    <p className="text-slate-400 text-sm">المبلغ الفعلي</p>
                    <p className="text-3xl font-bold text-purple-400">{formatCurrency(receiptData.actualAmount)}</p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${
                    receiptData.difference > 0 ? 'bg-green-500/10 border border-green-500/30' :
                    receiptData.difference < 0 ? 'bg-red-500/10 border border-red-500/30' :
                    'bg-blue-500/10 border border-blue-500/30'
                  }`}>
                    <p className="text-slate-400 text-xs">الفرق</p>
                    <p className={`text-xl font-bold ${
                      receiptData.difference > 0 ? 'text-green-400' :
                      receiptData.difference < 0 ? 'text-red-400' : 'text-blue-400'
                    }`}>
                      {receiptData.difference > 0 ? "+" : ""}{formatCurrency(receiptData.difference)}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">الموظف المسؤول</p>
                  <p className="text-white font-medium">{receiptData.employeeName}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">التاريخ والوقت</p>
                  <p className="text-white font-medium text-sm">{receiptData.date}</p>
                </div>
              </div>
              
              {receiptData.notes && (
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">ملاحظات</p>
                  <p className="text-white">{receiptData.notes}</p>
                </div>
              )}
              
              <div className="border-t border-dashed border-slate-700 pt-4 text-center">
                <p className="text-slate-500 text-xs">التوقيع: _____________________</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-3 pt-4 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowReceipt(false)}
              className="border-slate-600 text-slate-300"
            >
              إغلاق
            </Button>
            <Button
              onClick={() => shareCashboxViaWhatsApp(receiptData, user?.shop_name)}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 me-2" />
              واتساب
            </Button>
            <Button
              onClick={() => {
                const printContent = document.getElementById('cash-receipt-print');
                if (printContent) {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <!DOCTYPE html>
                    <html dir="rtl">
                    <head>
                      <meta charset="UTF-8">
                      <title>وصل صندوق - ${receiptData?.receiptNumber}</title>
                      <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
                        .receipt { border: 2px solid #333; padding: 20px; border-radius: 10px; }
                        .header { text-align: center; border-bottom: 2px dashed #333; padding-bottom: 15px; margin-bottom: 15px; }
                        .header h1 { font-size: 20px; margin-bottom: 5px; }
                        .shop-name { font-size: 22px; font-weight: bold; color: #7c3aed; margin-bottom: 8px; }
                        .receipt-num { font-size: 24px; font-weight: bold; color: #b45309; }
                        .section { margin: 10px 0; padding: 10px; background: #f5f5f5; border-radius: 5px; }
                        .amount-section { text-align: center; background: #e8f5e9; padding: 15px; border-radius: 10px; margin: 15px 0; }
                        .amount { font-size: 28px; font-weight: bold; color: #2e7d32; }
                        .signature { margin-top: 30px; padding-top: 20px; border-top: 1px dashed #333; text-align: center; }
                        .date { text-align: center; color: #666; font-size: 12px; margin-top: 10px; }
                      </style>
                    </head>
                    <body>
                      <div class="receipt">
                        <div class="header">
                          <div class="shop-name">${user?.shop_name || "اسم المؤسسة"}</div>
                          <h1>${receiptData?.type === 'open' ? 'وصل فتح الصندوق' : 'وصل إغلاق الصندوق'}</h1>
                          <div class="receipt-num">${receiptData?.receiptNumber}</div>
                        </div>
                        ${receiptData?.type === 'open' ? `
                          <div class="amount-section">
                            <p>المبلغ الافتتاحي</p>
                            <div class="amount">${formatCurrency(receiptData?.amount)}</div>
                          </div>
                        ` : `
                          <div class="section"><strong>الافتتاحي:</strong> ${formatCurrency(receiptData?.openingAmount)}</div>
                          <div class="section"><strong>المبيعات:</strong> +${formatCurrency(receiptData?.cashSales)}</div>
                          <div class="section"><strong>المصروفات:</strong> -${formatCurrency(receiptData?.expenses)}</div>
                          <div class="section"><strong>المتوقع:</strong> ${formatCurrency(receiptData?.expectedAmount)}</div>
                          <div class="amount-section">
                            <p>المبلغ الفعلي</p>
                            <div class="amount">${formatCurrency(receiptData?.actualAmount)}</div>
                          </div>
                          <div class="section"><strong>الفرق:</strong> ${receiptData?.difference > 0 ? '+' : ''}${formatCurrency(receiptData?.difference)}</div>
                        `}
                        <div class="section"><strong>الموظف:</strong> ${receiptData?.employeeName}</div>
                        ${receiptData?.notes ? `<div class="section"><strong>ملاحظات:</strong> ${receiptData?.notes}</div>` : ''}
                        <div class="signature">التوقيع: _____________________</div>
                        <div class="date">${receiptData?.date}</div>
                      </div>
                    </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Printer className="w-4 h-4 me-2" />
              طباعة الوصل
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
